import React from 'react';
import { 
  User, Briefcase, MapPin, Award, TrendingUp, 
  Layers, ShieldCheck, Mail, Phone, 
  DollarSign, CheckCircle2, AlertCircle, Users, 
  Target, Compass, Building, Star, LucideIcon, FileText, ChevronRight,
  Crown, BarChart3, Globe, GraduationCap, CalendarCheck, Landmark, Languages,
  Sparkles, Hash, BriefcaseBusiness
} from 'lucide-react';

export interface CandidateData {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  city: string;
  professionalHeadline: string;
  profilePhotoUrl: string | null;
  currentRole: string;
  currentCompany: string;
  totalYearsOfExperience: number | null;
  roleLevel: string;
  targetRole: string[];
  ownerShipLevel: string[];
  teamSizeLed: string | number | null;
  hiringAuthority: string[];
  productScale: string[];
  technologyStack?: string[];
  architectureExperience: string[];
  businessImpactType: string[];
  businessImpactDescription: string[];
  complexProblemSolved: string[];
  topAchievements: string[];
  domainExpertise: string[];
  coreExpertise: string[];
  reputationSignals: string[];
  workStyle: string[];
  careerIntent: string[];
  availabilityStatus: string;
  workFlexibility: string;
  decisionSpeed: string;
  notableCompanies: string[];
  professionalPitch: string;
  interviewReadiness: string;
  startupRiskPreference: string[];
  leadershipScore: string | number | "";
  architectureScore: string | number;
  businessImpactScore: string | number;
  achievementScore?: string | number;
  overallScore: string | number;
  profileCompleteScore?: string | number;
  currentCompensation: number | null;
  expectedCompensation: number | null;
  minimumAcceptableCompensation: number | null;
  noticePeriodDays: number | null;
  // Leadership & Executive Scope
  yearsInLeadership?: number | null;
  reportsToLevel?: string;
  budgetManagedLakhs?: number | null;
  revenueScopeCrores?: number | null;
  boardOrInvestorExposure?: boolean;
  travelWillingnessPercent?: number | null;
  highestEducation?: string;
  functionScope?: string[];
  geographicScope?: string[];
  companyStageExperience?: string[];
  transformationExperience?: string[];
  executiveEducation?: string[];
  languages?: string[];
  linkedInUrl: string;
  workEmail: string | null;
  phoneNumber: string;
  linkedinVerified: boolean | null;
  workEmailVerified: boolean | null;
  phoneVerified: boolean | null;
  profileVerified: boolean | null;
  profileComplete: boolean;
  createdAt: string;
  updatedAt: string;
  userEmail: string;
  userRole: string;
  isUserVerified: boolean;
  userStatus: string;
  canViewContactDetails: boolean;
}

interface RecruiterCandidateViewProps {
  data: CandidateData | null | undefined;
}

interface TagListProps {
  items: string[] | undefined | null;
  variant?: 'slate' | 'indigo' | 'cyan' | 'emerald' | 'amber' | 'rose';
}

interface SectionHeaderProps {
  icon: LucideIcon;
  title: string;
  iconColor?: string;
}

const isValidEmail = (email?: string | null) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

const getEmailDisplay = (email?: string | null) => {
  if (!email?.trim()) return "Not Available";
  return isValidEmail(email) ? email : "Invalid Email";
};

const getPhoneDisplay = (phone?: string | null) => {
  if (!phone?.trim()) return "Not Available";
  return phone;
};

const getCompanyDisplay = (company?: string | null) => {
  if (!company?.trim()) {
    return "Not Available";
  }
  return company;
};

const CandidateProfileView: React.FC<RecruiterCandidateViewProps> = ({ data }) => {
  if (!data) return <div className="p-10 text-center text-slate-500 font-medium bg-slate-950 border border-slate-900 rounded-2xl">No candidate data found.</div>;

  const TagList: React.FC<TagListProps> = ({ items, variant = 'slate' }) => {
    if (!items || items.length === 0) return <span className="text-[10px] text-slate-500 italic">Not specified</span>;
    
    const colorClasses = {
      slate: "bg-slate-800/40 text-slate-300 border-slate-700/60",
      indigo: "bg-indigo-950/40 text-indigo-300 border-indigo-800/50",
      cyan: "bg-cyan-950/40 text-cyan-300 border-cyan-800/50",
      emerald: "bg-emerald-950/40 text-emerald-300 border-emerald-800/50",
      amber: "bg-amber-950/40 text-amber-300 border-amber-800/50",
      rose: "bg-rose-950/40 text-rose-300 border-rose-800/50"
    };

    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {items.map((item, idx) => (
          <span key={idx} className={`px-2.5 py-1 rounded-md text-xs font-semibold uppercase tracking-wider border ${colorClasses[variant]}`}>
            {item?.replace(/_/g, ' ') || "N/A"}
          </span>
        ))}
      </div>
    );
  };

  const SectionHeader: React.FC<SectionHeaderProps> = ({ icon: Icon, title, iconColor = "text-indigo-400" }) => (
    <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-2">
      <div className={`p-1.5 rounded bg-slate-900 border border-slate-800/60`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <h3 className="text-xs font-black uppercase text-slate-200 tracking-widest">{title}</h3>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 bg-slate-950 min-h-screen font-sans antialiased text-slate-300 selection:bg-cyan-500/20">
      
      {/* EXECUTIVE HEADER BLOCK */}
      <div className="bg-slate-900 rounded-t-2xl border-t border-x border-slate-800 p-6 md:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 left-10 w-60 h-60 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex flex-col md:flex-row justify-between items-start gap-6">
          <div className="flex flex-col sm:flex-row items-start gap-5">
            <div className="w-24 h-24 bg-gradient-to-tr from-slate-800 to-slate-900 rounded-xl border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-xl relative group">
              {data.profilePhotoUrl ? (
                <img src={data.profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-slate-500" />
              )}
            </div>
            
            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-3xl font-black tracking-tight text-white">
                    {(data.firstName || data.lastName) ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : "Anonymous Candidate"}
                </h1>
                {data.availabilityStatus && (
                    <span className="bg-emerald-950 text-emerald-400 border border-emerald-800 px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase shadow-sm">
                    {data.availabilityStatus.replace(/_/g, ' ')}
                    </span>
                )}
              </div>

              <p className="text-lg text-slate-300 font-bold mt-1.5">
                {data.currentRole || "Professional"}
                <span className="text-slate-500 font-normal"> at </span>
                <span className="text-cyan-400 font-black">
                {data.canViewContactDetails
                    ? getCompanyDisplay(data.currentCompany)
                    : "********"}
                </span>
              </p>
              
              <div className="flex flex-wrap gap-3 mt-4 text-xs font-medium text-slate-400">
                <span className="flex items-center gap-1.5 bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-800">
                    <MapPin size={14} className="text-rose-400" /> {data.city || "Remote / Not Specified"}
                </span>
                <span className="flex items-center gap-1.5 bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-800">
                    <Briefcase size={14} className="text-cyan-400" /> <span><strong className="text-white font-bold">{data.totalYearsOfExperience ?? 0} Years</strong> Exp</span>
                </span>
                <span className="bg-indigo-950/60 text-indigo-300 border border-indigo-800/80 px-3 py-1.5 rounded-lg font-black uppercase tracking-widest text-[10px]">
                  Level: {data.roleLevel || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* RECRUITER ACTION CARD */}
          <div className="w-full md:w-80 bg-slate-950/60 border border-slate-800 p-5 rounded-xl flex flex-col justify-between self-stretch shadow-2xl backdrop-blur-sm">
            <div>
                <SectionHeader
                    icon={Phone}
                    title="Candidate Contact"
                    iconColor="text-slate-400"
                />

                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2.5 text-xs font-mono">
                    {/* Personal Email */}
                    <p className="flex items-center gap-2 text-slate-300 truncate">
                    <Mail size={13} className="text-indigo-400 flex-shrink-0" />
                    {data.canViewContactDetails
                        ? getEmailDisplay(data.userEmail)
                        : "********@*****.com"}
                    </p>

                    {/* Phone */}
                    <p className="flex items-center gap-2 text-slate-300">
                    <Phone size={13} className="text-emerald-400 flex-shrink-0" />
                    {data.canViewContactDetails
                        ? getPhoneDisplay(data.phoneNumber)
                        : "**********"}
                    </p>

                    {/* Profile Link */}
                    <p
                    className={`flex items-center gap-2 font-sans font-bold truncate mt-1 pt-2 border-t border-slate-800/80 ${
                        data.canViewContactDetails && data.linkedInUrl
                        ? "text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer"
                        : "text-slate-600 cursor-not-allowed"
                    }`}
                    >
                    <User size={13} className="text-indigo-400 flex-shrink-0" />
                    {data.canViewContactDetails
                        ? (data.linkedInUrl ? "View LinkedIn Profile" : "No URL Provided")
                        : "Profile URL Hidden"}
                    </p>
                </div>
            </div>

            <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500 mt-4 md:mt-0 pt-2 border-t border-slate-800/60 flex justify-between gap-4">
              <span>Status: <strong className="text-indigo-400">{(data.interviewReadiness || "PENDING").replace(/_/g, ' ')}</strong></span>
              <span>Updated: {data.updatedAt ? new Date(data.updatedAt).toLocaleDateString() : 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* RECRUITER SNAPSHOT - WHAT MATTERS FIRST */}
      <div className="bg-slate-900 border-x border-slate-850 px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3 shadow-xl relative overflow-hidden border-b border-slate-800">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-500" />
        <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Users size={12} className="text-indigo-400" /> Team Led</div>
          <div className="text-lg font-black mt-0.5">{data.teamSizeLed || "0"} <span className="text-slate-400 text-xs">{Number(data.teamSizeLed) > 1 ? "People" : "Person"}</span></div>
        </div>
        <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><BriefcaseBusiness size={12} className="text-cyan-400" /> Years Exp</div>
          <div className="text-lg font-black mt-0.5">{data.totalYearsOfExperience ?? 0}</div>
        </div>
        <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><Crown size={12} className="text-amber-400" /> Reports To</div>
          <div className="text-xs font-bold mt-0.5 uppercase truncate">{data.reportsToLevel || "N/A"}</div>
        </div>
        <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><MapPin size={12} className="text-rose-400" /> Location</div>
          <div className="text-xs font-bold mt-0.5 truncate">{data.city || "Remote/Hybrid"}</div>
        </div>
        {((data.functionScope?.length || 0) + (data.targetRole?.length || 0) + (data.coreExpertise?.length || 0)) > 0 && (
          <div className="col-span-2 md:col-span-4 mt-1 grid grid-cols-1 md:grid-cols-3 gap-3">
            {data.functionScope && data.functionScope.length > 0 && (
              <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5"><Briefcase size={12} className="text-indigo-400" /> Functions Led</div>
                <div className="flex flex-wrap gap-1">
                  {data.functionScope.slice(0, 6).map((t, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-indigo-950/30 border border-indigo-800/50 text-indigo-200 text-[10px] uppercase tracking-wider">{String(t).replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {data.targetRole && data.targetRole.length > 0 && (
              <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                <div className="text-[10px] font-black text-cyan-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5"><Target size={12} className="text-cyan-400" /> Target Roles</div>
                <div className="flex flex-wrap gap-1">
                  {data.targetRole.slice(0, 6).map((t, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-cyan-950/30 border border-cyan-800/50 text-cyan-200 text-[10px] uppercase tracking-wider">{String(t).replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {data.coreExpertise && data.coreExpertise.length > 0 && (
              <div className="bg-slate-950/40 border border-slate-800 rounded-lg px-3 py-2">
                <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1.5 mb-1.5"><Layers size={12} className="text-emerald-400" /> Core Stack</div>
                <div className="flex flex-wrap gap-1">
                  {data.coreExpertise.slice(0, 6).map((t, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-emerald-950/30 border border-emerald-800/50 text-emerald-200 text-[10px] uppercase tracking-wider">{String(t).replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* LEADERSHIP VITAL STATISTICS HERO STRIP */}
      <div className="bg-slate-900 border-x border-slate-850 px-6 py-5 grid grid-cols-2 sm:grid-cols-4 gap-4 shadow-xl text-center sm:text-left relative overflow-hidden border-b border-slate-800">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-indigo-500 via-cyan-500 to-emerald-500" />
        <div className="border-r border-slate-800 last:border-0 pr-2">
          <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1.5">
            <Award size={12} className="text-emerald-400" /> Leadership Index
          </div>
          <div className="text-xl font-black tracking-tight mt-1 text-emerald-400">{String(data.leadershipScore || "0")}%</div>
        </div>
        <div className="border-r border-slate-800 last:border-0 pr-2">
          <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1.5">
            <Layers size={12} className="text-cyan-400" /> Architecture Index
          </div>
          <div className="text-xl font-black tracking-tight mt-1 text-cyan-400">{String(data.architectureScore || "0")}%</div>
        </div>
        <div className="border-r border-slate-800 last:border-0 pr-2">
          <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1.5">
            <TrendingUp size={12} className="text-amber-400" /> Business Impact
          </div>
          <div className="text-xl font-black tracking-tight mt-1 text-amber-400">{String(data.businessImpactScore || "0")}%</div>
        </div>
        <div>
          <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest flex items-center justify-center sm:justify-start gap-1.5">
            <Sparkles size={12} className="text-indigo-400" /> Overall Score
          </div>
          <div className="text-xl font-black tracking-tight mt-1 text-indigo-400">{String(data.overallScore || "0")}%</div>
        </div>
      </div>

      {/* TWO COLUMN COMPACT RESUME LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 bg-slate-900 border-x border-b border-slate-800 rounded-b-2xl shadow-2xl overflow-hidden divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
        
        {/* RESUME BODY: MAIN CONTENT (LEFT 2/3) */}
        <div className="lg:col-span-2 bg-slate-900/40 p-6 md:p-8 space-y-8">
          
          <div>
            <SectionHeader icon={Crown} title="Executive Summary" iconColor="text-indigo-400" />
            <div className="mt-4 space-y-4">
              <div className="border-l-2 border-cyan-500 pl-4 bg-cyan-950/10 py-2 pr-2 rounded-r-lg">
                <h4 className="text-base sm:text-lg font-bold italic tracking-tight bg-gradient-to-r from-cyan-300 to-indigo-300 bg-clip-text text-transparent leading-snug">
                  "{data.professionalHeadline || "Results-driven executive ready to drive scalable growth and technical excellence."}"
                </h4>
              </div>
              <div className="bg-slate-950/60 p-4 sm:p-5 rounded-xl border border-slate-800/80 shadow-inner">
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-medium whitespace-pre-line">
                  {data.professionalPitch || "No executive pitch provided."}
                </p>
              </div>
            </div>
          </div>

          {/* KEY PROOF POINTS - RECRUITER FIRST SCAN */}
          {(data.topAchievements && data.topAchievements.length > 0) && (
            <div>
              <SectionHeader icon={Star} title="Top Achievements (Proof of Impact)" iconColor="text-amber-400" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {data.topAchievements.slice(0, 6).map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-950/40 border border-slate-800 rounded-lg">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-200 leading-tight">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LEADERSHIP & EXECUTIVE SECTION - NEW */}
          {(data.yearsInLeadership || data.reportsToLevel || data.budgetManagedLakhs || data.revenueScopeCrores || (data.functionScope && data.functionScope.length > 0) || (data.geographicScope && data.geographicScope.length > 0) || (data.companyStageExperience && data.companyStageExperience.length > 0) || (data.transformationExperience && data.transformationExperience.length > 0) || (data.executiveEducation && data.executiveEducation.length > 0) || (data.languages && data.languages.length > 0)) && (
            <div>
              <SectionHeader icon={Crown} title="Leadership & Executive Scope" iconColor="text-indigo-400" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-950/40 p-4 rounded-xl border border-slate-800 space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><CalendarCheck size={12} className="text-indigo-400" /> Years in Leadership</span>
                    <span className="font-bold text-slate-200">{data.yearsInLeadership ?? "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><Landmark size={12} className="text-amber-400" /> Reports To</span>
                    <span className="font-bold text-slate-200 uppercase text-[11px]">{data.reportsToLevel || "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><DollarSign size={12} className="text-emerald-400" /> Budget Managed (Lakhs)</span>
                    <span className="font-bold text-slate-200">{data.budgetManagedLakhs ?? "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><BarChart3 size={12} className="text-cyan-400" /> Revenue Scope (Cr)</span>
                    <span className="font-bold text-slate-200">{data.revenueScopeCrores ?? "N/A"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><Compass size={12} className="text-cyan-400" /> Travel Willingness</span>
                    <span className="font-bold text-slate-200">{data.travelWillingnessPercent ?? "N/A"}{data.travelWillingnessPercent ? "%" : ""}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><Building size={12} className="text-cyan-400" /> Board/Investor Exposure</span>
                    <span className={`font-bold ${data.boardOrInvestorExposure ? 'text-emerald-400' : 'text-slate-500'}`}>{data.boardOrInvestorExposure ? "Yes" : "No"}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 flex items-center gap-1.5"><GraduationCap size={12} className="text-cyan-400" /> Highest Education</span>
                    <span className="font-bold text-slate-200">{data.highestEducation || "N/A"}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {data.functionScope && data.functionScope.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Functions Led</span>
                      <TagList items={data.functionScope} variant="indigo" />
                    </div>
                  )}
                  {data.geographicScope && data.geographicScope.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5 mb-1"><Globe size={12} className="text-cyan-400" /> Geographic Scope</span>
                      <TagList items={data.geographicScope} variant="cyan" />
                    </div>
                  )}
                  {data.companyStageExperience && data.companyStageExperience.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Company Stage Experience</span>
                      <TagList items={data.companyStageExperience} variant="amber" />
                    </div>
                  )}
                  {data.transformationExperience && data.transformationExperience.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Transformation Experience</span>
                      <TagList items={data.transformationExperience} variant="rose" />
                    </div>
                  )}
                  {data.executiveEducation && data.executiveEducation.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Executive Education</span>
                      <TagList items={data.executiveEducation} variant="slate" />
                    </div>
                  )}
                  {data.languages && data.languages.length > 0 && (
                    <div>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5 mb-1"><Languages size={12} className="text-slate-400" /> Languages</span>
                      <TagList items={data.languages} variant="slate" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Expanded Achievements if more exist */}
          {data.topAchievements && data.topAchievements.length > 6 && (
            <div>
              <SectionHeader icon={Star} title="Additional Leadership & Architectural Accomplishments" iconColor="text-amber-400" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.topAchievements.slice(6).map((achievement, i) => (
                  <div key={i} className="flex items-start gap-3 p-3.5 bg-slate-950/40 border border-slate-800 rounded-xl shadow-sm">
                    <Award className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-200 leading-tight">{achievement}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quantifiable Business Impact Framework */}
          <div>
            <SectionHeader icon={TrendingUp} title="Proven Business Impact Metrics" iconColor="text-emerald-400" />
            <div className="mb-3">
              <TagList items={data.businessImpactType} variant="emerald" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.businessImpactDescription && data.businessImpactDescription.length > 0 ? (
                data.businessImpactDescription.map((impact, i) => (
                    <div key={i} className="p-4 bg-emerald-950/10 rounded-xl border-l-2 border-emerald-500 shadow-sm flex gap-2 border-y border-r border-slate-800">
                    <span className="text-xs text-slate-300 font-medium leading-relaxed italic">"{impact}"</span>
                    </div>
                ))
              ) : (
                <div className="col-span-2 text-xs text-slate-500 italic">Description of business impact is unavailable.</div>
              )}
            </div>
          </div>

          {/* Complex System Problems Solved */}
          <div>
            <SectionHeader icon={ShieldCheck} title="Complex Problems Overcome" iconColor="text-rose-400" />
            <div className="space-y-2.5">
              {data.complexProblemSolved && data.complexProblemSolved.length > 0 ? (
                data.complexProblemSolved.map((problem, i) => (
                    <div key={i} className="flex gap-2.5 text-xs font-medium text-slate-300 bg-rose-950/10 p-3.5 rounded-xl border border-rose-950/60 leading-relaxed">
                    <ChevronRight size={14} className="text-rose-400 flex-shrink-0 mt-0.5" />
                    <span>{problem}</span>
                    </div>
                ))
              ) : (
                <div className="text-xs text-slate-500 italic">Case studies of complex problem solving not provided.</div>
              )}
            </div>
          </div>

          {/* Scope of Technical Execution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-800/60">
            <div>
              <SectionHeader icon={Building} title="Product Architecture Exposure" iconColor="text-cyan-400" />
              <div className="space-y-4">
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Scale Thresholds</span>
                  <TagList items={data.productScale} variant="cyan" />
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Architectural Experience</span>
                  <TagList items={data.architectureExperience} variant="indigo" />
                </div>
              </div>
            </div>
            
            <div>
              <SectionHeader icon={Compass} title="Culture & Alignment" iconColor="text-amber-400" />
              <div className="space-y-4">
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Company Stage Preference</span>
                  <TagList items={data.startupRiskPreference} variant="amber" />
                </div>
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Operational Workstyle</span>
                  <TagList items={data.workStyle} variant="slate" />
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* RESUME SIDEBAR: TARGETING & METRICS (RIGHT 1/3) */}
        <div className="bg-slate-950/40 p-6 md:p-8 space-y-6">
          
          {/* Target Architecture Parameters */}
          <div>
            <SectionHeader icon={Target} title="Career Target Objectives" iconColor="text-indigo-400" />
            <div className="space-y-4 text-xs">
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Desired Roles</span>
                <TagList items={data.targetRole} variant="indigo" />
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Ownership Mandate</span>
                <TagList items={data.ownerShipLevel} variant="slate" />
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Hiring Scope</span>
                <TagList items={data.hiringAuthority} variant="cyan" />
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Career Progression Intent</span>
                <TagList items={data.careerIntent} variant="rose" />
              </div>
            </div>
          </div>

          {/* Expert Domains & Core Tooling Matrix */}
          <div>
            <SectionHeader icon={Layers} title="Skill & Industry Matrix" iconColor="text-cyan-400" />
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Core Engineering Toolset</span>
                <TagList items={data.coreExpertise} variant="cyan" />
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Industrial Verticals</span>
                <TagList items={data.domainExpertise} variant="slate" />
              </div>
            </div>
          </div>

          {/* Corporate Background Track Record */}
          {data.notableCompanies && data.notableCompanies.length > 0 && (
            <div>
              <SectionHeader icon={Building} title="Notable Work History" iconColor="text-cyan-400" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.notableCompanies.map((company, i) => (
                  <span key={i} className="px-2.5 py-1 bg-slate-900 border border-slate-800 text-slate-300 rounded-md text-xs font-bold shadow-sm">
                    {company || "N/A"}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Compensation & Logistics Parameters */}
          <div>
            <SectionHeader icon={DollarSign} title="Compensation & Logistics" iconColor="text-emerald-400" />
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2.5 text-xs font-semibold shadow-2xl">
              <div className="flex justify-between border-b border-slate-800/60 pb-2">
                <span className="text-slate-400">Notice Period</span>
                <span className="font-bold text-slate-200">{data.noticePeriodDays ?? "N/A"} Days</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-2">
                <span className="text-slate-400">Target Range Expected</span>
                <span className="font-bold text-emerald-400">₹{data.expectedCompensation || "0"}k/yr</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-2">
                <span className="text-slate-400">Floor Minimum Acceptable</span>
                <span className="font-bold text-rose-400">₹{data.minimumAcceptableCompensation || "0"}k/yr</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/60 pb-2">
                <span className="text-slate-400">Work Flexibility</span>
                <span className="font-bold text-slate-200 uppercase text-[11px]">{data.workFlexibility || "NOT SPECIFIED"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Decision Agility</span>
                <span className="font-bold text-indigo-400 uppercase text-[11px]">{data.decisionSpeed || "STANDARD"}</span>
              </div>
            </div>
          </div>

          {/* Secure Background Integrity Metrics */}
          <div>
            <SectionHeader icon={ShieldCheck} title="System Verifications" iconColor="text-emerald-400" />
            <div className="grid grid-cols-2 gap-2 text-[10px] font-black tracking-wider uppercase">
              {[
                { checked: !!data.isUserVerified, text: "System Identity" },
                { checked: !!data.linkedinVerified, text: "LinkedIn Verified" },
                { checked: !!data.workEmailVerified, text: "Corporate Mail" },
                { checked: !!data.phoneVerified, text: "Phone Cleared" },
                { checked: !!data.profileVerified, text: "Background Check" },
                { checked: !!data.profileComplete, text: "Profile Completed" }
              ].map((ver, idx) => (
                <div key={idx} className={`p-2 rounded-lg border flex items-center gap-1.5 transition ${ver.checked ? 'bg-emerald-950/40 border-emerald-900/80 text-emerald-400 shadow-sm' : 'bg-slate-900/20 border-slate-800/60 text-slate-600'}`}>
                  {ver.checked ? <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" /> : <AlertCircle size={12} className="flex-shrink-0 text-slate-700" />}
                  <span className="truncate">{ver.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CandidateProfileView;